import { useState, type ReactNode } from 'react'
import { Modal } from '../modal/Modal.tsx'
import { Button, type ButtonVariant } from '../button/Button.tsx'
import styles from './ConfirmDialog.module.css'

export interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  /**
   * Runs the action. Return a promise and the dialog waits for it: the confirm
   * button spins, nothing can be clicked, and it closes once the work is done —
   * so an action that takes a moment can't be fired twice or look stuck.
   */
  onConfirm: () => unknown
  title: string
  /**
   * What the confirmation is about. A string is the common case and stays
   * styled as muted body text; rich content is for a confirmation that has to
   * show what it is about — the matches an admin is about to abandon, say —
   * rather than only describe it.
   */
  description?: ReactNode
  /** Rendered below the description. Use for lists, links, or a nested warning. */
  children?: ReactNode
  confirmLabel?: string
  /**
   * An icon before the confirm label. A primary action carries one, so the
   * button reads as the thing to do at a glance.
   */
  confirmIcon?: ReactNode
  cancelLabel?: string
  variant?: 'danger' | 'default'
  /** Holds the confirm button shut — for a decision still loading its context. */
  confirmDisabled?: boolean
  /**
   * A second way through, rendered between Cancel and the confirm button — for
   * a decision with two acceptable outcomes ("Withdraw them and start" vs
   * "Start anyway"). Like confirm, clicking it runs `onClick` then closes.
   */
  secondaryAction?: ConfirmDialogSecondaryAction
}

export interface ConfirmDialogSecondaryAction {
  label: string
  /** Like `onConfirm`: return a promise and the dialog waits for it. */
  onClick: () => unknown
  /** Default 'secondary', so the confirm button stays the heavier choice. */
  variant?: ButtonVariant
  loading?: boolean
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  confirmIcon,
  cancelLabel = 'Cancel',
  variant = 'default',
  confirmDisabled = false,
  secondaryAction,
}: ConfirmDialogProps) {
  // Which button's work is in flight, while the dialog waits on it.
  const [busy, setBusy] = useState<'confirm' | 'secondary' | null>(null)

  // A second press can't start the work twice: every button is disabled as
  // soon as the dialog starts waiting.
  const run = async (which: 'confirm' | 'secondary', action: () => unknown) => {
    const result = action()
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      setBusy(which)
      try {
        await result
      } catch {
        // The caller reports its own failure; the dialog only stops waiting.
      } finally {
        setBusy(null)
      }
    }
    onClose()
  }
  const handleConfirm = () => { void run('confirm', onConfirm) }
  // Nothing closes the dialog while it waits — not Cancel, Escape or the backdrop.
  const close = () => { if (!busy) onClose() }

  return (
    <Modal
      open={open}
      onClose={close}
      size="sm"
      title={title}
      showCloseButton={false}
      footer={
        <div className={styles.actions}>
          <Button variant="ghost" size="sm" onClick={close} disabled={busy !== null}>
            {cancelLabel}
          </Button>
          {secondaryAction && (
            <Button
              variant={secondaryAction.variant ?? 'secondary'}
              size="sm"
              onClick={() => { void run('secondary', secondaryAction.onClick) }}
              loading={secondaryAction.loading || busy === 'secondary'}
              disabled={busy === 'confirm'}
            >
              {secondaryAction.label}
            </Button>
          )}
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            size="sm"
            onClick={handleConfirm}
            disabled={confirmDisabled || busy === 'secondary'}
            loading={busy === 'confirm'}
          >
            {confirmIcon}
            {confirmLabel}
          </Button>
        </div>
      }
    >
      {/* A <div> rather than a <p>: rich description content may include lists
          or links, which a paragraph cannot legally contain. */}
      {description && <div className={styles.description}>{description}</div>}
      {children && <div className={styles.content}>{children}</div>}
    </Modal>
  )
}
